import * as Sentry from '@sentry/browser';
import { QueryClientProvider } from '@tanstack/react-query';
import 'antd/dist/reset.css';
import 'array-flat-polyfill';
import React from 'react';
import 'react-app-polyfill/stable';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';

import App from './App';
import { AuthHandler } from './AuthHandler';
import { initializeFirebaseApp } from './api/firebase';
import ErrorBoundary from './components/ErrorBoundary';
import FlashMessage from './components/flashMessage';
import InternetNotification from './components/internetNotification/InternetNotification';
import { initializeSessionReplay } from './helpers/amplitude';
import { analytics } from './helpers/analytics';
import { globalQueryClient, isProduction } from './helpers/extra';
import './i18n';
import './index.scss';
import store from './store/store';

// Error tracking sdk
Sentry.init({
  dsn: isProduction
    ? 'https://a28f9d4e0592488ba73d58e0552929fb@o391419.ingest.sentry.io/5237501'
    : null,
  environment: process.env.REACT_APP_ENV,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0.5,
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications.',
    'The error you provided does not contain a stack trace.',
    // https://github.com/mapbox/mapbox-gl-js/issues/8480
    'AbortError: Fetch is aborted',
    'Error while drawing area selection polygon',
  ],
});

initializeFirebaseApp();

// Initialize Session Replay with Segment integration
// This should be initialized early to capture all user interactions
initializeSessionReplay();

// Expose analytics to window for testing/debugging
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.analytics = analytics;
}

// Runs early to detect and handle preload errors when the
// app attempts to load outdated or missing code-split chunks.
// https://vite.dev/guide/build#load-error-handling
window.addEventListener('vite:preloadError', () => {
  window.location.reload();
});

const root = createRoot(document.getElementById('root'));

root.render(
  <Provider store={store}>
    <ErrorBoundary>
      <AuthHandler>
        <QueryClientProvider client={globalQueryClient}>
          <App />
          <FlashMessage />
          <InternetNotification />
        </QueryClientProvider>
      </AuthHandler>
    </ErrorBoundary>
  </Provider>,
);
